import { useEffect, useState } from 'react'
import { ArrowLeft, FilePen, Save, Trash2 } from 'lucide-react'
import { cmsApi } from '../lib/api'
import { newProfileForm, profileFilled, profileFormOf, profileProblem, saveProfileForm, storedProfileForm } from '../lib/catalog'
import { useCatalogDraft } from '../lib/useCatalogDraft'
import type { Place, ProducerKind, ProducerUsage, ProfileForm } from '../types'
import { CatalogNotice } from './CatalogNotice'
import { LeaveDialog } from './LeaveDialog'
import { LoadingState } from './LoadingState'
import { ProfileFields } from './ProfileFields'
import { KINDS, usageSummary } from './catalogForm'

type Props = {
  producerId: string | null
  // A new profile's draft, continued here (PRD.md, item 98).
  draftId: string | null
  // Where the CMS's menu asks the editor before leaving it.
  exit: { current: ((go: () => void) => void) | null }
  // The places a venue can be, from the CMS's bootstrap.
  places: Place[]
  canEdit: boolean
  catalogEditing: boolean
  onBack: () => void
  onSaved: (message: string) => void
  onError: (message: string) => void
  onNotice: (message: string) => void
}

export function ProducerEditor({ producerId, draftId, exit, places, canEdit, catalogEditing, onBack, onSaved, onError, onNotice }: Props) {
  const editable = canEdit && catalogEditing
  const [form, setForm] = useState<ProfileForm | null>(producerId || draftId ? null : newProfileForm())
  const [saved, setSaved] = useState<ProfileForm | null>(null)
  const [usage, setUsage] = useState<ProducerUsage | null>(null)
  const [saving, setSaving] = useState(false)
  // A photo link typed and not yet prepared, or a file being prepared.
  const [imagePending, setImagePending] = useState(false)
  const [error, setError] = useState('')
  const [leaving, setLeaving] = useState<(() => void) | null>(null)
  const draft = useCatalogDraft({
    kind: 'producer', draftId, value: form, filled: Boolean(form && profileFilled(form)), enabled: !producerId && editable,
    store: storedProfileForm, onStored: (kept) => setForm((current) => current && { ...current, image_url: kept.image_url }),
  })

  useEffect(() => {
    let active = true
    if (producerId) {
      cmsApi.getProducer(producerId)
        .then((entry) => {
          if (!active) return
          const opened = profileFormOf(entry.producer)
          setForm(opened)
          setSaved(opened)
          setUsage(entry.usage)
        })
        .catch((requestError) => { if (active) setError(requestError instanceof Error ? requestError.message : 'Não foi possível abrir o perfil.') })
    } else if (draftId) {
      cmsApi.getCatalogDraft<ProfileForm>(draftId)
        .then((entry) => {
          if (!active) return
          draft.opened(entry.content, entry.updated_at)
          setForm(entry.content)
        })
        .catch((requestError) => { if (active) setError(requestError instanceof Error ? requestError.message : 'Não foi possível abrir o rascunho.') })
    }
    return () => { active = false }
  }, [producerId, draftId])

  const changed = Boolean(form && saved && JSON.stringify(form) !== JSON.stringify(saved))
  const asksOnLeave = !form || !editable ? false
    : producerId ? changed
      : !draftId && (draft.id.current !== null || profileFilled(form))
  const leave = (go: () => void) => (asksOnLeave ? setLeaving(() => go) : go())
  useEffect(() => {
    exit.current = leave
    return () => { exit.current = null }
  })

  useEffect(() => {
    const waiting = form && editable && (producerId ? changed : profileFilled(form) && draft.behind(form))
    if (!waiting) return
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = '' }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  })

  if (error) return <div className="error-state"><p>{error}</p><button className="button button--secondary" onClick={onBack}>Voltar</button></div>
  if (!form) return <LoadingState label="Abrindo perfil…" />

  const update = (patch: Partial<ProfileForm>) => setForm((current) => current && { ...current, ...patch })

  const save = async () => {
    const problem = imagePending ? 'A foto ainda não foi preparada: toque em Buscar imagem e espere ficar pronta.' : profileProblem(form)
    if (problem) { onError(problem); return }
    setSaving(true)
    try {
      if (!producerId) await draft.close()
      await saveProfileForm(form, { producerId, draftId: draft.id.current })
      onSaved(producerId ? 'Perfil atualizado.' : 'Perfil criado.')
    } catch (requestError) {
      draft.reopen()
      onError(requestError instanceof Error ? requestError.message : 'Não foi possível salvar o perfil.')
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!producerId || !window.confirm(`Excluir “${form.name}”? Essa ação não pode ser desfeita.`)) return
    setSaving(true)
    try {
      await cmsApi.deleteProducer(producerId)
      onSaved('Perfil excluído.')
    } catch (requestError) {
      onError(requestError instanceof Error ? requestError.message : 'Não foi possível excluir o perfil.')
      setSaving(false)
    }
  }

  const deleteDraft = async (then: () => void) => {
    try {
      await draft.discard()
      if (draft.id.current) onNotice('Rascunho excluído.')
    } catch (requestError) {
      draft.reopen()
      onError(requestError instanceof Error ? requestError.message : 'Não foi possível excluir o rascunho.')
      return
    }
    then()
  }

  const saveDraftNow = async (then: () => void = onBack) => {
    try {
      await draft.saveNow(form)
    } catch (requestError) {
      onError(requestError instanceof Error ? requestError.message : 'Não foi possível salvar o rascunho.')
      return
    }
    onNotice('Rascunho salvo.')
    then()
  }

  const blocked = usage ? usageSummary(usage) : ''
  const disabled = !editable || saving
  const primary = <button className="button button--primary" disabled={saving} onClick={() => void save()}><Save size={18} /> {saving ? 'Salvando…' : producerId ? 'Salvar perfil' : 'Criar perfil'}</button>

  return (
    <div className="page-stack editor-page">
      {leaving && (
        <LeaveDialog
          kind={producerId ? 'changes' : 'draft'}
          entity="producer"
          onDrop={producerId ? leaving : () => deleteDraft(leaving)}
          onKeep={() => saveDraftNow(leaving)}
          onClose={() => setLeaving(null)}
        />
      )}
      <section className="editor-heading">
        <button className="icon-button" onClick={() => leave(onBack)} aria-label="Voltar"><ArrowLeft size={20} /></button>
        <div><h1>{producerId ? form.name || 'Perfil' : 'Criar perfil'}</h1><p>Nome, @, foto e contatos da página do perfil no app.</p></div>
        {editable && <div className="editor-heading__actions">
          {producerId && <button className="icon-button icon-button--danger" disabled={saving || Boolean(blocked)} title={blocked ? `Não pode ser excluído: ${blocked}.` : 'Excluir perfil'} onClick={() => void remove()} aria-label="Excluir perfil"><Trash2 size={18} /></button>}
          {primary}
        </div>}
      </section>

      <CatalogNotice catalogEditing={catalogEditing} />

      <section className="panel form-section">
        <div className="form-grid">
          <label className="field"><span>Tipo</span>
            <select aria-label="Tipo do perfil" disabled={disabled} value={form.kind} onChange={(event) => update({ kind: event.target.value as ProducerKind })}>
              {KINDS.map((kind) => <option key={kind.value} value={kind.value}>{kind.label}</option>)}
            </select>
            <small>Todo tipo aparece igual na linha "Com…" dos eventos.</small>
          </label>
          {form.kind === 'venue' && (
            <label className="field"><span>Local</span>
              <select aria-label="Local do perfil" disabled={disabled} value={form.place_id ?? ''} onChange={(event) => update({ place_id: event.target.value || null })}>
                <option value="">Escolha o local</option>
                {places.map((place) => <option key={place.id} value={place.id}>{place.name}{place.city ? ` · ${place.city}` : ''}</option>)}
              </select>
              <small>A página do perfil mostra o endereço e o que este local oferece.</small>
            </label>
          )}
        </div>
        <ProfileFields value={form} disabled={disabled} onChange={update} onPendingImage={setImagePending} onError={onError} />
      </section>

      {producerId && usage && <p className="catalog-usage">{blocked ? `Em uso: ${blocked}. Por isso não pode ser excluído.` : 'Nenhum evento, seguidor ou fonte de ingestão usa este perfil.'}</p>}

      {editable && (!producerId || changed) && (
        <div className="editor-footer" role="region" aria-label="Alterações não salvas">
          <span role="status">{producerId ? 'Alterações não salvas' : draft.label}</span>
          <div>
            {producerId
              ? <button className="button button--secondary" disabled={saving} onClick={() => { if (saved && window.confirm('Descartar as alterações não salvas? O perfil continua como está no app.')) setForm(saved) }}>Descartar alterações</button>
              : <>
                <button className="button button--secondary" disabled={saving} onClick={() => { if (window.confirm('Excluir este rascunho? Ele sai da lista de rascunhos.')) void deleteDraft(onBack) }}>Excluir rascunho</button>
                <button className="button button--secondary" disabled={saving || draft.state.status === 'saving'} onClick={() => void saveDraftNow()}><FilePen size={17} /> Salvar rascunho</button>
              </>}
            {primary}
          </div>
        </div>
      )}
    </div>
  )
}
